import { Box, HStack, VStack } from '@chakra-ui/react'
import type { ReactNode } from 'react'
import { FaLockOpen } from 'react-icons/fa'

import { ContextRail } from '@/components/navigation/ContextRail'
import type { NavRail } from '@/components/navigation/nav.types'
import { Button } from '@/components/ui/button'
import { UserRoutes } from '@/routes/_hooks/routes.enum'
import { Link } from '@/routing'

import type { UserMenuContentProps } from '../UserMenuContent.types'

type UserMenuDrawerPrimaryNavProps = Pick<
  UserMenuContentProps,
  | 'user'
  | 'captureEvent'
  | 't'
  | 'endImpersonation'
  | 'isImpersonating'
  | 'onClose'
> & {
  rowPx: number
  rowPy: number
  rowGap: number
  /** The very rail the desktop sidebar shows, for the space named by the URL. */
  rail: NavRail
  wrapWithClose: (fn?: () => void) => () => void
}

/**
 * The mobile drawer's navigation: the rail itself.
 *
 * It used to be a hand-maintained echo of the rail — "Mon matériel", "Mes réservations", the
 * personal-only entries — assembled from `useContextualPath` with its own idea of which context
 * was active. Two lists, two mechanisms, one of them always a release behind the other.
 *
 * Now both surfaces render {@link ContextRail} over `useContextRail()`, so a section added to
 * the rail appears here the same day, with the same labels and the same active row. The click
 * handler sits on the wrapper rather than on each link: any navigation inside closes the drawer,
 * including the settings door and the way back out.
 */
export const UserMenuDrawerPrimaryNav = ({
  rowPx,
  rowPy,
  rowGap,
  user,
  captureEvent,
  t,
  rail,
  endImpersonation,
  isImpersonating,
  onClose,
  wrapWithClose,
}: UserMenuDrawerPrimaryNavProps) => {
  const ButtonRow = ({
    onClick,
    children,
  }: {
    onClick: () => void
    children: ReactNode
  }) => (
    <Button
      onClick={onClick}
      px={rowPx}
      py={rowPy}
      w="full"
      textAlign="left"
      cursor="pointer"
      _hover={{ bg: 'gray.100' }}
      _dark={{ _hover: { bg: 'gray.800' } }}
    >
      {children}
    </Button>
  )

  return (
    <VStack align="stretch" gap={0}>
      <Box onClick={() => onClose?.()}>
        <ContextRail rail={rail} />
      </Box>

      {isImpersonating ? (
        <ButtonRow
          onClick={() => {
            endImpersonation()
            captureEvent('navigation_end_impersonation_clicked', {
              user_id: user?.id,
            })
            onClose?.()
          }}
        >
          <HStack gap={rowGap}>
            <FaLockOpen />
            <span>
              {t(
                'admin:accountManagement.users.actions.endImpersonation.label'
              )}
            </span>
          </HStack>
        </ButtonRow>
      ) : (
        <Link
          to={UserRoutes.Logout}
          onClick={wrapWithClose(() =>
            captureEvent('navigation_logout_clicked', {
              destination: UserRoutes.Logout,
              user_id: user?.id,
            })
          )}
          style={{ display: 'block', width: '100%' }}
        >
          <Box
            px={rowPx}
            py={rowPy}
            _hover={{ bg: 'gray.100' }}
            _dark={{ _hover: { bg: 'gray.800' } }}
            cursor="pointer"
          >
            <HStack gap={rowGap}>
              <FaLockOpen />
              <span>{t('menu.logout')}</span>
            </HStack>
          </Box>
        </Link>
      )}
    </VStack>
  )
}
